import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { Pagination } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "./ServitorTransactionHistory.css";
import { URL } from "../../config";
import Loader from "../../Components/LoaderSpin/Loader";

const ServitorTransactionHistory = () => {
  const { t } = useTranslation("servitor");
  const { servitor } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialPage = parseInt(params.get("page") || "1", 10);
  const { pathname, search } = location;
  const navigateRef = React.useRef(navigate);

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const transactionsPerPage = 10;

  useEffect(() => {
    if (servitor && servitor.Id) {
      fetch(`${URL}/servitors/${servitor.Id}/transactions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          const txnsArray = Array.isArray(data.transactions)
            ? data.transactions
            : [];
          setTransactions(txnsArray);
          setLoading(false);
        })
        .catch(() => {
          setTransactions([]);
          setLoading(false);
        });
    }
  }, [servitor]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    params.set("page", currentPage);
    navigateRef.current(
      { pathname, search: params.toString() },
      { replace: true }
    );
  }, [currentPage, pathname, search]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = d.toLocaleString("default", { month: "long" });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const indexOfLast = currentPage * transactionsPerPage;
  const indexOfFirst = indexOfLast - transactionsPerPage;
  const currentTransactions = transactions.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(transactions.length / transactionsPerPage);
  const totalTxns = transactions.length;
  const startTxn = indexOfFirst + 1;
  const endTxn = Math.min(indexOfLast, totalTxns);

  // if (loading)
  //   return <p className="servitortransaction-loading">{t("common.loading")}</p>;

  const getTxnStatusLabel = (txn) => {
    if (txn.txnType === "bonus") return "Bonus Added";
    if (txn.txnType === "reward_claim") return "Reward Claimed";
    if (txn.txnType === "service_payment") return "Service Completed";
    return txn.status || "Balance Adjustment";
  };

  const getStatusClass = (txn) => {
    if (txn.txnType === "bonus" || txn.txnType === "reward_claim")
      return "servitortransaction-status-bonus";
    if (txn.txnType === "service_payment")
      return "servitortransaction-status-service";
    return "servitortransaction-status-default";
  };

  return (
    <div className="servitortransaction-container">
      <Loader show={loading} />
      <div className="servitortransaction-header">
        {/* <h1 className="servitortransaction-title">Transaction History</h1> */}
        <h1 className="servitortransaction-title">{t("transactions.title")}</h1>
      </div>

      {transactions.length === 0 ? (
        <p className="servitortransaction-empty">
          {/* No transaction history available. */}
          {t("transactions.empty")}
        </p>
      ) : (
        <>
          <div className="servitortransaction-content">
            {/* Desktop Table */}
            <div className="servitortransaction-desktop">
              <div className="servitortransaction-table-wrapper">
                <table className="servitortransaction-table">
                  <thead>
                    <tr>
                      {/* <th className="servitortransaction-th">Order No</th>
                      <th className="servitortransaction-th">Service</th>
                      <th className="servitortransaction-th">Type</th>
                      <th className="servitortransaction-th">Amount</th>
                      <th className="servitortransaction-th">Commission Fee</th>
                      <th className="servitortransaction-th">Bonus</th>
                      <th className="servitortransaction-th">Net</th>
                      <th className="servitortransaction-th">Date</th> */}

                      <th className="servitortransaction-th">{t("transactions.table.orderNo")}</th>
                      <th className="servitortransaction-th">{t("transactions.table.service")}</th>
                      <th className="servitortransaction-th">{t("transactions.table.type")}</th>
                      <th className="servitortransaction-th">{t("transactions.table.amount")}</th>
                      <th className="servitortransaction-th">{t("transactions.table.platform_support_fee")}</th>
                      <th className="servitortransaction-th">{t("transactions.table.bonus")}</th>
                      <th className="servitortransaction-th">{t("transactions.table.net")}</th>
                      <th className="servitortransaction-th">{t("transactions.table.date")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTransactions.map((txn, index) => (
                      <tr key={index} className="servitortransaction-row">
                        <td className="servitortransaction-td">
                          {txn.order_number || "-"}
                        </td>
                        <td className="servitortransaction-td">
                          {txn.serviceType || "-"}
                        </td>
                        <td className="servitortransaction-td">
                          <span
                            className={`servitortransaction-status ${getStatusClass(
                              txn
                            )}`}
                          >
                            {getTxnStatusLabel(txn)}
                          </span>
                        </td>
                        <td className="servitortransaction-td servitortransaction-amount">
                          ৳ {Number(txn.totalAmount || 0).toFixed(2)}
                        </td>
                        <td
                          className={`servitortransaction-td ${
                            txn.commissionAmount > 0
                              ? "servitortransaction-negative"
                              : "servitortransaction-positive"
                          }`}
                        >
                          {txn.commissionAmount > 0 ? "-" : "+"}৳{" "}
                          {Math.abs(Number(txn.commissionAmount || 0)).toFixed(2)}
                        </td>
                        <td
                          className={`servitortransaction-td ${
                            Number(txn.servitorBonus) > 0
                              ? "servitortransaction-bonus"
                              : ""
                          }`}
                        >
                          {Number(txn.servitorBonus) > 0
                            ? `+৳ ${Number(txn.servitorBonus).toFixed(2)}`
                            : "-"}
                        </td>
                        <td className="servitortransaction-td">
                          ৳ {Number(txn.netAmount || 0).toFixed(2)}
                        </td>
                        <td className="servitortransaction-td">
                          {formatDate(txn.transactionDate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card */}
            <div className="servitortransaction-mobile">
              {currentTransactions.map((txn, index) => (
                <div key={index} className="servitortransaction-card">
                  <div className="servitortransaction-card-row">
                    <div className="servitortransaction-card-field">
                      <span className="servitortransaction-card-label">
                        {/* Order No: */}
                        {t("transactions.table.orderNo")}:
                      </span>
                      <span className="servitortransaction-card-value">
                        {txn.order_number || "-"}
                      </span>
                    </div>
                    <div className="servitortransaction-card-field-right">
                      <span className="servitortransaction-card-label">
                        {/* Date: */}
                        {t("transactions.table.date")}:
                      </span>
                      <span className="servitortransaction-card-value">
                        {formatDate(txn.transactionDate)}
                      </span>
                    </div>
                  </div>
                  <div className="servitortransaction-card-row">
                    <div className="servitortransaction-card-field">
                      <span className="servitortransaction-card-label">
                        {/* Service: */}
                        {t("transactions.table.service")}:
                      </span>
                      <span className="servitortransaction-card-value">
                        {txn.serviceType || "-"}
                      </span>
                    </div>
                    <div className="servitortransaction-card-field-right">
                      <span className="servitortransaction-card-label">
                        {/* Type: */}
                        {t("transactions.table.type")}:
                      </span>
                      <span
                        className={`servitortransaction-status ${getStatusClass(
                          txn
                        )}`}
                      >
                        {getTxnStatusLabel(txn)}
                      </span>
                    </div>
                  </div>
                  <div className="servitortransaction-card-row">
                    <div className="servitortransaction-card-field">
                      <span className="servitortransaction-card-label">
                        {/* Amount: */}
                        {t("transactions.table.amount")}:
                      </span>
                      <span className="servitortransaction-card-amount">
                        ৳ {Number(txn.totalAmount || 0).toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="servitortransaction-card-field-right">
                      <span className="servitortransaction-card-label">
                        {/* Bonus: */}
                        {t("transactions.table.bonus")}:
                      </span>
                      <span
                        className={`servitortransaction-card-bonus ${
                          Number(txn.servitorBonus) > 0
                            ? "servitortransaction-bonus"
                            : ""
                        }`}
                      >
                        {Number(txn.servitorBonus) > 0
                          ? `+৳ ${Number(txn.servitorBonus).toFixed(2)}`
                          : "-"}
                      </span>
                    </div>
                  </div>
                  <div className="servitortransaction-card-row">
                    <div className="servitortransaction-card-field">
                      <span className="servitortransaction-card-label">
                        {/* Net: */}
                        {t("transactions.table.net")}:
                      </span>
                      <span className="servitortransaction-card-value">
                        ৳ {Number(txn.netAmount || 0).toFixed(2)}
                      </span>
                    </div>

                    <div className="servitortransaction-card-field-right">
                      <span className="servitortransaction-card-label">
                        {/* Commission: */}
                        {t("transactions.table.platform_support_fee")}:
                      </span>
                      <span className="servitortransaction-card-commission">
                        -৳ {Number(txn.commissionAmount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="servitortransaction-summary">
            {/* Showing {startTxn}–{endTxn} of {totalTxns} transactions */}
            {t("transactions.pagination.showing", {
              startTxn,
              endTxn,
              totalTxns,
              defaultValue: `Showing ${startTxn}–${endTxn} of ${totalTxns} transactions`
            })}
          </div>

          <div className="servitortransaction-pagination">
            <Pagination>
              <Pagination.First
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {/* Previous */}
                {t("transactions.pagination.prev")}
              </Pagination.Prev>
              <Pagination.Next
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                {/* Next */}
                {t("transactions.pagination.next")}
              </Pagination.Next>
              <Pagination.Last
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
};

export default ServitorTransactionHistory;
